import { useState, useEffect, useRef, useCallback } from 'react';

export interface DataSubscription {
  id: string;
  endpoint: string;
  params?: Record<string, any>;
  transformFn?: (data: any) => any;
  errorHandler?: (error: Error) => void;
}

export interface RealTimeDataState<T = any> {
  data: T | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  lastUpdate: Date | null;
  reconnectCount: number;
}

export interface UseRealTimeDataOptions {
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
  autoConnect?: boolean;
  bufferSize?: number;
}

export interface UseRealTimeDataReturn<T = any> extends RealTimeDataState<T> {
  connect: () => void;
  disconnect: () => void;
  subscribe: (subscription: DataSubscription) => string;
  unsubscribe: (subscriptionId: string) => void;
  send: (message: any) => void;
  clearError: () => void;
  getConnectionState: () => WebSocket['readyState'] | null;
  subscribeMulti?: (subscriptions: DataSubscription[]) => string[];
  computeDeltaSeries?: (series: Array<{ timestamp: Date; value: number }>) => Array<{ timestamp: Date; value: number; delta: number }>;
}

const DEFAULT_OPTIONS: Required<UseRealTimeDataOptions> = {
  reconnectInterval: 5000,
  maxReconnectAttempts: 5,
  heartbeatInterval: 30000,
  autoConnect: true,
  bufferSize: 100,
};

export const useRealTimeData = <T = any>(
  wsUrl?: string,
  options: UseRealTimeDataOptions = {}
): UseRealTimeDataReturn<T> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  const [state, setState] = useState<RealTimeDataState<T>>({
    data: null,
    isConnected: false,
    isLoading: false,
    error: null,
    lastUpdate: null,
    reconnectCount: 0,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const subscriptionsRef = useRef<Map<string, DataSubscription>>(new Map());
  const dataBufferRef = useRef<T[]>([]);
  const reconnectAttemptsRef = useRef(0);

  // Generate subscription ID
  const generateSubscriptionId = () => {
    return `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Clear timeouts
  const clearTimeouts = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  };

  // Start heartbeat
  const startHeartbeat = () => {
    clearTimeouts();
    heartbeatTimeoutRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, opts.heartbeatInterval);
  };

  // Handle WebSocket message
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data);
      
      // Handle pong response
      if (message.type === 'pong') {
        return;
      }

      // Handle subscription data
      if (message.subscriptionId) {
        const subscription = subscriptionsRef.current.get(message.subscriptionId);
        if (subscription) {
          let processedData = message.data;
          
          // Apply transformation if provided
          if (subscription.transformFn) {
            processedData = subscription.transformFn(processedData);
          }
          
          // Update buffer
          dataBufferRef.current.unshift(processedData);
          if (dataBufferRef.current.length > opts.bufferSize) {
            dataBufferRef.current = dataBufferRef.current.slice(0, opts.bufferSize);
          }
          
          setState(prev => ({
            ...prev,
            data: processedData,
            lastUpdate: new Date(),
            error: null,
          }));
          
          return;
        }
      }

      // Handle general data updates
      setState(prev => ({
        ...prev,
        data: message.data || message,
        lastUpdate: new Date(),
        error: null,
      }));
      
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to parse message data',
      }));
    }
  }, [opts.bufferSize]);

  // Handle WebSocket error
  const handleError = useCallback((error: Event) => {
    console.error('WebSocket error:', error);
    setState(prev => ({
      ...prev,
      error: 'Connection error occurred',
      isConnected: false,
    }));
  }, []);

  // Handle WebSocket close
  const handleClose = useCallback((event: CloseEvent) => {
    setState(prev => ({
      ...prev,
      isConnected: false,
      isLoading: false,
    }));

    clearTimeouts();

    // Attempt reconnection if not a clean close
    if (!event.wasClean && reconnectAttemptsRef.current < opts.maxReconnectAttempts) {
      reconnectAttemptsRef.current++;
      setState(prev => ({
        ...prev,
        reconnectCount: reconnectAttemptsRef.current,
      }));

      reconnectTimeoutRef.current = setTimeout(() => {
        if (wsUrl) {
          connect();
        }
      }, opts.reconnectInterval);
    } else if (reconnectAttemptsRef.current >= opts.maxReconnectAttempts) {
      setState(prev => ({
        ...prev,
        error: 'Maximum reconnection attempts reached',
      }));
    }
  }, [opts.maxReconnectAttempts, opts.reconnectInterval]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!wsUrl) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setState(prev => ({
          ...prev,
          isConnected: true,
          isLoading: false,
          error: null,
        }));
        
        reconnectAttemptsRef.current = 0;
        startHeartbeat();
        
        // Re-subscribe to existing subscriptions
        subscriptionsRef.current.forEach((subscription, id) => {
          ws.send(JSON.stringify({
            type: 'subscribe',
            subscriptionId: id,
            ...subscription,
          }));
        });
      };

      ws.onmessage = handleMessage;
      ws.onerror = handleError;
      ws.onclose = handleClose;

      wsRef.current = ws;
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Connection failed',
      }));
    }
  }, [wsUrl, handleMessage, handleError, handleClose]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    clearTimeouts();
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }

    setState(prev => ({
      ...prev,
      isConnected: false,
      isLoading: false,
    }));
    
    reconnectAttemptsRef.current = 0;
  }, []);

  // Subscribe to data updates
  const subscribe = useCallback((subscription: DataSubscription): string => {
    const subscriptionId = generateSubscriptionId();
    subscriptionsRef.current.set(subscriptionId, subscription);

    // Send subscription request if connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe',
        subscriptionId,
        ...subscription,
      }));
    }

    return subscriptionId;
  }, []);

  // Unsubscribe from data updates
  const unsubscribe = useCallback((subscriptionId: string) => {
    subscriptionsRef.current.delete(subscriptionId);

    // Send unsubscribe request if connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'unsubscribe',
        subscriptionId,
      }));
    }
  }, []);

  // Send message to WebSocket
  const send = useCallback((message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      setState(prev => ({
        ...prev,
        error: 'Not connected to WebSocket',
      }));
    }
  }, []);

  // Clear error state
  const clearError = useCallback(() => {
    setState(prev => ({
      ...prev,
      error: null,
    }));
  }, []);

  // Get connection state
  const getConnectionState = useCallback(() => {
    return wsRef.current?.readyState || null;
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (opts.autoConnect && wsUrl) {
      connect();
    }

    return () => {
      clearTimeouts();
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [wsUrl, opts.autoConnect, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeouts();
      disconnect();
    };
  }, [disconnect]);

  return {
    ...state,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    send,
    clearError,
    getConnectionState,
    subscribeMulti: (subs: DataSubscription[]) => {
      const ids: string[] = [];
      subs.forEach(s => ids.push(subscribe(s)));
      return ids;
    },
    computeDeltaSeries: (series: Array<{ timestamp: Date; value: number }>) => {
      const result: Array<{ timestamp: Date; value: number; delta: number }> = [];
      let prev = 0;
      for (const point of series) {
        const delta = point.value - prev;
        result.push({ timestamp: point.timestamp, value: point.value, delta });
        prev = point.value;
      }
      return result;
    }
  };
};

// Helper hook for specific data subscriptions
export const useDataSubscription = <T = any>(
  wsUrl: string,
  subscription: Omit<DataSubscription, 'id'>,
  options?: UseRealTimeDataOptions
) => {
  const realTimeData = useRealTimeData<T>(wsUrl, options);
  const subscriptionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (realTimeData.isConnected && !subscriptionIdRef.current) {
      subscriptionIdRef.current = realTimeData.subscribe({
        id: generateSubscriptionId(),
        ...subscription,
      });
    }

    return () => {
      if (subscriptionIdRef.current) {
        realTimeData.unsubscribe(subscriptionIdRef.current);
        subscriptionIdRef.current = null;
      }
    };
  }, [realTimeData.isConnected, realTimeData.subscribe, realTimeData.unsubscribe, subscription]);

  return realTimeData;
};

// Utility functions
const generateSubscriptionId = () => {
  return `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export default useRealTimeData;